import { Component, HostListener, OnInit } from '@angular/core';
import { registerLocaleData, NgIf, AsyncPipe, Location } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import { difference, hasIn, keys } from 'lodash';

import { ENLANG } from './lang/en';
import { DELANG } from './lang/de';

import { environment } from './../environments/environment';
import { RegistryService } from './../app/@shared/services/registry.service';
import { LocalstorageService } from '@gridscale/gs-services/localstorage.service';
import { ZendeskService } from '@gridscale/gs-services/zendesk.service';

//NGRX
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';

import { isLoggedIn, isLoggingIn } from './@store/auth/auth.selectors';

import { Subject } from 'rxjs';
import { debounceTime, filter, take, takeUntil } from 'rxjs/operators';

import { getFullSession } from './@store/user/user.selectors';

import * as _ from 'lodash';
import { ErrorHandlerService } from './@shared/services/errorhandler.service';
import { SessionService } from './../app/@shared/services/session.service';
import { LoaderModule } from '@gridscale/ingrid/components/loader';
import { TrackingService } from './@shared/services/tracking.service';
import { COMMIT_HASH } from '../commithash';
import { CurrencyService } from '@gridscale/ingrid/helper/services';
import { AppAvailabilityService } from './@shared/services/app-availability';
import { OverlayAlertService } from '@gridscale/ingrid/components/overlay-alert';
import { DebugCenterComponent } from './panel/_main-view/debug-center/debug-center.component';
import { A11yStatusComponent } from '@gridscale/ingrid/components/a11ystatus';

// the '<!--#echo....--> stuff is replace BY NGINX during runtime!
const appVersion = '<!--#echo var="ssiPACKAGE_VERSION"-->';
const gitCommitHash = COMMIT_HASH;
const deployment_environment = '<!--#echo var="ssiDEPLOYMENT_ENVIRONMENT"-->';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [
        A11yStatusComponent,
        AsyncPipe,
        LoaderModule,
        NgIf,
        RouterOutlet,
        TranslateModule,
    ]
})
export class AppComponent implements OnInit {
  @HostListener('window:keydown.control.alt.∂') onOpenDebugcenter(event: KeyboardEvent) {
    this.#openDebugCenter();
  }

  title = 'partnerpanel';
  loadingMessage = '';
  noSession = false;
  isLoggedIn$ = this.store.select(isLoggedIn);
  isLoggingIn$ = this.store.select(isLoggingIn);


  isAppBusy$ = this.availabilityService.isAppBusy$;

  private readonly destroyed$: Subject<void> = new Subject();


  constructor(
    router: Router,
    activatedRoute: ActivatedRoute,
    private registry: RegistryService,
    private zendeskService: ZendeskService,
    private store: Store,
    private readonly errorHandler: ErrorHandlerService,
    private readonly localStorage: LocalstorageService,
    readonly location: Location,
    readonly sessionService: SessionService,
    private readonly trackingService: TrackingService,
    private readonly currencyService: CurrencyService,
    private readonly availabilityService: AppAvailabilityService,
    private readonly overlayAlertService: OverlayAlertService
  ) {
    this.registry.set('gs_version_hash', gitCommitHash);
    this.registry.set('gs_version', appVersion);
    this.registry.set('gs_deploy_environment', deployment_environment);

    if (sessionService.redirectUrl) {
      router.navigate([sessionService.redirectUrl]);
      sessionService.redirectUrl = undefined;
    }




    // check language files
    const dekeys = keys(DELANG);
    const enkeys = keys(ENLANG);
    const onlyDE = difference(dekeys, enkeys);
    const onlyEN = difference(enkeys, dekeys);

    if (onlyDE.length) {
      this.errorHandler.captureMessage('Translation-Key only existing in DE: ' + onlyDE.join(', '));
      if (!environment.production) console.warn('Translation-Key only existing in DE: ' + onlyDE.join(', '));
    }
    if (onlyEN.length) {
      this.errorHandler.captureMessage('Translation-Key only existing in EN: ' + onlyEN.join(', '));
      if (!environment.production) console.warn('Translation-Key only existing in EN: ' + onlyEN.join(', '));
    }

    registerLocaleData(localeEn, 'en');
    registerLocaleData(localeDe, 'de');

    // check if route has nosession data flag and do not show the loader until session loaded cause it will not be loaded
    router.events.subscribe(val => {
      if (val instanceof NavigationEnd) {
        if (hasIn(activatedRoute, ['root', 'firstChild', 'snapshot', 'data', 'nosession'])) {
          this.noSession = activatedRoute.root?.firstChild?.snapshot.data.nosession;
        } else {
          this.noSession = false;
        }

        this.trackingService.pushTag({
          event: 'page',
          pageName: val.url
        });
      }
    });

    // GD-15347: A11y: After routing we always focus the main header element to not confuse sightless people
    router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      debounceTime(100)
    ).subscribe(() => {
      document.querySelector('header')?.focus();
    })


    // Ticket validation

    const ticketToken = new URL(window.location.href).searchParams.get('validateticket');

    if (ticketToken) {
      this.zendeskService.storeToken(ticketToken);
      this.localStorage.setItem('validateticket', ticketToken);
    }


  }

  ngOnInit() {
    this.store
      .select(getFullSession)
      .pipe(
        filter(fullSession => fullSession.user !== null && !!fullSession.partner),
        take(1)
      )
      .subscribe(fullSession => {
        this.registry.set('currentPartner', fullSession.partner);
        this.registry.set('currentUser', fullSession.user);

        if (fullSession.partner?.object_uuid) {
          this.currencyService.setCurrentPartnerUuid(fullSession.partner?.object_uuid);
        }
      });

    this.availabilityService.busyMessage$
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe(message => {
        this.loadingMessage = message;
      })
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  #openDebugCenter() {
    this.overlayAlertService.custom(
      'DEBUG CENTER',
      DebugCenterComponent,
      [
        {
          label: 'Get me outta here!',
          mode: 'primary',
          keyShortcut: 'Escape',
          callback: (button, alert) => {
            alert.close();
          }
        }
      ],
      {},
      'fal fa-user-secret',
      'It\'s Magic!',
      '1000px'
    );
  }
}
